import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Badge, Button, Card, Form, Modal, Spinner, Table } from 'react-bootstrap';
import { toast } from 'react-toastify';
import PageLayout from '../../layouts/PageLayout';
import { employeeAPI, salaryAdvanceAPI } from '../../services';
import { useBranch } from '../../hooks';

/**
 * Ứng lương qua link: quản lý tạo link riêng cho nhân viên xin ứng, nhân viên mở link trên app
 * nhân viên gửi số tiền, quản lý chuyển tiền rồi bấm Duyệt ⇒ khoản ứng tự trừ vào bảng lương kỳ.
 */

const STATUS = {
  PENDING: { label: 'Chờ chuyển tiền', variant: 'warning' },
  LINK_SENT: { label: 'Đã gửi link', variant: 'info' },
  APPROVED: { label: 'Đã duyệt', variant: 'success' },
  REJECTED: { label: 'Từ chối', variant: 'danger' },
  REVOKED: { label: 'Đã thu hồi', variant: 'secondary' },
};

const TABS = [
  { key: 'PENDING', label: 'Chờ duyệt' },
  { key: 'LINK_SENT', label: 'Link chưa dùng' },
  { key: '', label: 'Tất cả' },
];

const money = (v) => (v === null || v === undefined ? '-' : `${Math.round(Number(v)).toLocaleString('vi-VN')}đ`);

const dateTime = (iso) =>
  iso
    ? new Date(iso).toLocaleString('vi-VN', {
        timeZone: 'Asia/Ho_Chi_Minh',
        day: '2-digit',
        month: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      })
    : '-';

const monthLabel = (m) => (m ? `${m.slice(5, 7)}/${m.slice(0, 4)}` : '-');

async function copyText(text) {
  try {
    await navigator.clipboard.writeText(text);
    toast.success('Đã sao chép link');
  } catch {
    window.prompt('Sao chép link này gửi nhân viên', text);
  }
}

/** Chỉ gắn vào cây khi đang mở, nên mỗi lần mở là một form trống mới. */
function LinkModal({ onHide, onCreated }) {
  const { selectedBranch } = useBranch();
  const [employees, setEmployees] = useState([]);
  const [employeeId, setEmployeeId] = useState('');
  const [created, setCreated] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    employeeAPI
      .getEmployees({ limit: 100, status: 'ACTIVE', branch_id: selectedBranch?.id || undefined })
      .then((res) => {
        const list = Array.isArray(res?.data) ? res.data : Array.isArray(res) ? res : [];
        setEmployees(list.sort((a, b) => (a.full_name || '').localeCompare(b.full_name || '', 'vi')));
      })
      .catch(() => setEmployees([]));
  }, [selectedBranch?.id]);

  const create = async () => {
    if (!employeeId || saving) return;
    setSaving(true);
    try {
      const res = await salaryAdvanceAPI.createRequestLink(employeeId);
      setCreated(res);
      onCreated();
    } catch (e) {
      toast.error(e?.message || 'Không tạo được link');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      show
      onHide={onHide}
      size="lg"
      centered
    >
      <Modal.Header closeButton>
        <Modal.Title>Tạo link ứng lương</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {created ? (
          <>
            <div className="mb-2">
              Link cho <b>{created.employee?.full_name}</b>, hết hạn {dateTime(created.link_expires_at)}
            </div>
            {created.link_url ? (
              <Form.Control
                as="textarea"
                rows={3}
                readOnly
                value={created.link_url}
                onFocus={(e) => e.target.select()}
              />
            ) : (
              <div className="text-danger">Máy chủ chưa cấu hình địa chỉ app nhân viên (STAFF_APP_URL)</div>
            )}
          </>
        ) : (
          <Form.Select
            value={employeeId}
            onChange={(e) => setEmployeeId(e.target.value)}
          >
            <option value="">Chọn nhân viên</option>
            {employees.map((e) => (
              <option
                key={e.id}
                value={e.id}
              >
                {e.full_name}
              </option>
            ))}
          </Form.Select>
        )}
      </Modal.Body>
      <Modal.Footer>
        {created ? (
          <Button
            disabled={!created.link_url}
            onClick={() => copyText(created.link_url)}
          >
            Sao chép link
          </Button>
        ) : (
          <Button
            disabled={!employeeId || saving}
            onClick={create}
          >
            {saving ? (
              <Spinner
                size="sm"
                className="me-2"
              />
            ) : null}
            Tạo link
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
}

function DecideModal({ request, mode, onHide, onDone }) {
  const [amount, setAmount] = useState(request.amount ? String(Math.round(request.amount)) : '');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const approve = mode === 'approve';

  const submit = async () => {
    setSaving(true);
    try {
      if (approve) {
        const value = Number(String(amount).replace(/\D/g, ''));
        await salaryAdvanceAPI.approveRequest(request.id, {
          ...(value && value !== Math.round(request.amount) ? { amount: value } : {}),
          ...(note.trim() ? { note: note.trim() } : {}),
        });
        toast.success('Đã duyệt — khoản ứng sẽ trừ vào bảng lương kỳ ' + monthLabel(request.month));
      } else {
        await salaryAdvanceAPI.rejectRequest(request.id, note.trim() ? { note: note.trim() } : {});
        toast.success('Đã từ chối yêu cầu');
      }
      onDone();
    } catch (e) {
      toast.error(e?.message || 'Không xử lý được yêu cầu');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      show
      onHide={onHide}
      centered
    >
      <Modal.Header closeButton>
        <Modal.Title>{approve ? 'Duyệt ứng lương' : 'Từ chối ứng lương'}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="mb-3">
          <div className="fw-bold">{request.employee?.full_name}</div>
          <div className="text-muted">
            Xin ứng {money(request.amount)} · lương kỳ {monthLabel(request.month)} tới lúc xin{' '}
            {money(request.earned_at_request)}
          </div>
          {request.employee_note ? <div className="mt-1">“{request.employee_note}”</div> : null}
        </div>
        {approve ? (
          <Form.Group className="mb-3">
            <Form.Label>Số tiền đã chuyển</Form.Label>
            <Form.Control
              inputMode="numeric"
              value={amount ? Number(String(amount).replace(/\D/g, '')).toLocaleString('vi-VN') : ''}
              onChange={(e) => setAmount(e.target.value)}
            />
          </Form.Group>
        ) : null}
        <Form.Control
          placeholder={approve ? 'Ghi chú (không bắt buộc)' : 'Lý do từ chối (nhân viên sẽ thấy)'}
          value={note}
          maxLength={255}
          onChange={(e) => setNote(e.target.value)}
        />
      </Modal.Body>
      <Modal.Footer>
        <Button
          variant={approve ? 'success' : 'danger'}
          disabled={saving}
          onClick={submit}
        >
          {saving ? (
            <Spinner
              size="sm"
              className="me-2"
            />
          ) : null}
          {approve ? 'Đã chuyển tiền — Duyệt' : 'Từ chối'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

function RequestRow({ r, onDecide, onRevoke }) {
  const st = r.expired ? { label: 'Link hết hạn', variant: 'secondary' } : STATUS[r.status];
  return (
    <tr>
      <td>
        <div className="fw-semibold">{r.employee?.full_name}</div>
        <div className="small text-muted">{r.employee?.phone_number || ''}</div>
      </td>
      <td>{monthLabel(r.month)}</td>
      <td className="text-end fw-semibold">{money(r.amount)}</td>
      <td className="text-end">{money(r.earned_at_request)}</td>
      <td className="small">{r.employee_note || r.decision_note || ''}</td>
      <td className="small">{dateTime(r.requested_at || r.created_at)}</td>
      <td>
        <Badge bg={st?.variant}>{st?.label}</Badge>
      </td>
      <td className="text-nowrap text-end">
        {r.status === 'PENDING' ? (
          <>
            <Button
              size="sm"
              variant="success"
              className="me-1"
              onClick={() => onDecide(r, 'approve')}
            >
              Duyệt
            </Button>
            <Button
              size="sm"
              variant="outline-danger"
              onClick={() => onDecide(r, 'reject')}
            >
              Từ chối
            </Button>
          </>
        ) : null}
        {r.status === 'LINK_SENT' && !r.expired ? (
          <>
            <Button
              size="sm"
              variant="outline-primary"
              className="me-1"
              disabled={!r.link_url}
              onClick={() => copyText(r.link_url)}
            >
              Sao chép link
            </Button>
            <Button
              size="sm"
              variant="outline-secondary"
              onClick={() => onRevoke(r)}
            >
              Thu hồi
            </Button>
          </>
        ) : null}
      </td>
    </tr>
  );
}

const HEADERS = ['Nhân viên', 'Kỳ lương', 'Xin ứng', 'Lương tới lúc xin', 'Ghi chú', 'Thời gian', 'Trạng thái', ''];

export default function SalaryAdvanceRequestsPage() {
  const [tab, setTab] = useState('PENDING');
  const [reloadKey, setReloadKey] = useState(0);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showLink, setShowLink] = useState(false);
  const [decide, setDecide] = useState(null);

  const reload = useCallback(() => setReloadKey((k) => k + 1), []);

  useEffect(() => {
    let alive = true;
    salaryAdvanceAPI
      .listRequests(tab ? { status: tab } : {})
      .then((res) => alive && setRows(Array.isArray(res) ? res : Array.isArray(res?.data) ? res.data : []))
      .catch((e) => {
        if (!alive) return;
        toast.error(e?.message || 'Không tải được danh sách ứng lương');
        setRows([]);
      })
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [tab, reloadKey]);

  const pendingTotal = useMemo(
    () => rows.filter((r) => r.status === 'PENDING').reduce((s, r) => s + Number(r.amount || 0), 0),
    [rows],
  );

  const revoke = async (r) => {
    if (!window.confirm(`Thu hồi link ứng lương của ${r.employee?.full_name}?`)) return;
    try {
      await salaryAdvanceAPI.revokeRequestLink(r.id);
      reload();
    } catch (e) {
      toast.error(e?.message || 'Không thu hồi được link');
    }
  };

  let body;
  if (loading) {
    body = (
      <div className="text-center py-4">
        <Spinner />
      </div>
    );
  } else if (rows.length === 0) {
    body = <div className="text-center text-muted py-4">Không có yêu cầu</div>;
  } else {
    body = (
      <Table
        responsive
        hover
        className="align-middle"
      >
        <thead>
          <tr>
            {HEADERS.map((h, i) => (
              <th
                key={h || i}
                className={i === 2 || i === 3 ? 'text-end' : undefined}
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <RequestRow
              key={r.id}
              r={r}
              onDecide={(req, mode) => setDecide({ request: req, mode })}
              onRevoke={revoke}
            />
          ))}
        </tbody>
      </Table>
    );
  }

  return (
    <PageLayout>
      <div className="mc-card">
        <div className="mc-breadcrumb d-flex justify-content-between align-items-center flex-wrap gap-2">
          <h3 className="mc-breadcrumb-title">Ứng lương</h3>
          <Button onClick={() => setShowLink(true)}>+ Tạo link ứng lương</Button>
        </div>
      </div>

      <Card className="shadow-sm mt-3">
        <Card.Body>
          <div className="d-flex gap-2 flex-wrap mb-3 align-items-center">
            {TABS.map((t) => (
              <Button
                key={t.key || 'all'}
                size="sm"
                variant={tab === t.key ? 'primary' : 'outline-primary'}
                onClick={() => {
                  if (t.key === tab) return;
                  setLoading(true);
                  setTab(t.key);
                }}
              >
                {t.label}
              </Button>
            ))}
            {tab === 'PENDING' && rows.length ? (
              <span className="ms-auto text-muted">
                Cần chuyển: <b>{money(pendingTotal)}</b>
              </span>
            ) : null}
          </div>
          {body}
        </Card.Body>
      </Card>

      {showLink ? (
        <LinkModal
          onHide={() => setShowLink(false)}
          onCreated={reload}
        />
      ) : null}
      {decide ? (
        <DecideModal
          key={decide.request.id + decide.mode}
          request={decide.request}
          mode={decide.mode}
          onHide={() => setDecide(null)}
          onDone={() => {
            setDecide(null);
            reload();
          }}
        />
      ) : null}
    </PageLayout>
  );
}
